import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import AuthLayout from '@components/auth/AuthLayout';
import useAuth from '@hooks/useAuth';
import useAsyncEffect from '@hooks/useAsyncEffect';

import { verifyCaptchaToken } from '@apis/auth';
import { getMe } from '@apis/users';

import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';

/**
 * Login form state
 */
type LoginForm = {
  username: string;
  password: string;
};

const INPUT_HEIGHT = 48;

const Login = () => {
  const [form, setForm] = useState<LoginForm>({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(true);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // Caps Lock detection for password input
  const [capsLockOn, setCapsLockOn] = useState(false);

  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [, setUser] = useRecoilState(userState);

  const [searchParams, setSearchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const returnTo = searchParams.get('returnTo');

  /**
   * Form validation (design-only: disables CTA visually)
   */
  const canSubmit = useMemo(() => {
    return (
      form.username.trim().length > 0 &&
      form.password.length > 0 &&
      !!executeRecaptcha &&
      !loading
    );
  }, [form.username, form.password, executeRecaptcha, loading]);

  const handleChange =
    (key: keyof LoginForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleForgotPassword = () => navigate('/forgot-password');

  /**
   * Remove auth-related query params after successful login
   */
  const cleanAuthParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (next.has('reason')) {
      next.delete('reason');
      changed = true;
    }
    if (next.has('returnTo')) {
      next.delete('returnTo');
      changed = true;
    }

    if (changed) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Post-login routing by role
   */
  const routeAfterLogin = useCallback(async () => {
    const me = await getMe();
    setUser(me);

    cleanAuthParams();

    if (returnTo && returnTo.startsWith('/')) {
      navigate(returnTo, { replace: true });
      return;
    }

    navigate(me.group === UserGroup.PARTNER ? '/orders' : '/home', {
      replace: true,
    });
  }, [navigate, setUser, cleanAuthParams, returnTo]);

  /**
   * Centralized navigation after authentication
   */
  useAsyncEffect(async () => {
    if (isAuthenticated) {
      await routeAfterLogin();
    }
  }, [isAuthenticated, routeAfterLogin]);

  /**
   * Login submit handler
   */
  const handleSubmit = useCallback(async () => {
    setErrorMsg(null);
    setLoading(true);

    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA is not ready. Please try again.');
      }

      const token = await executeRecaptcha('login');
      await verifyCaptchaToken(token);

      await login({ ...form, rememberMe });
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Login failed. Please try again.');
      setShowPassword(false);
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, form, rememberMe, login]);

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your admin or partner account to access the console."
      footer={
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Authorized access only.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            This site is protected by reCAPTCHA.
          </Typography>
        </Stack>
      }
    >
      <Stack spacing={3}>
        {/* Status / error messages */}
        {(reason === 'session_expired' || errorMsg) && (
          <Stack spacing={1.5}>
            {reason === 'session_expired' && (
              <Alert severity="warning">
                Your session has expired. Please sign in again.
              </Alert>
            )}
            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          </Stack>
        )}

        {/* Login form */}
        <Stack spacing={2}>
          <TextField
            label="Username"
            value={form.username}
            onChange={handleChange('username')}
            autoComplete="username"
            fullWidth
            disabled={loading}
            InputProps={{ sx: { height: INPUT_HEIGHT } }}
          />

          <TextField
            label="Password"
            value={form.password}
            onChange={handleChange('password')}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            fullWidth
            disabled={loading}
            InputProps={{
              sx: { height: INPUT_HEIGHT },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onKeyDown={(e) => {
              const caps = (e as any)?.getModifierState?.('CapsLock') === true;
              setCapsLockOn(caps);

              if (e.key === 'Enter' && canSubmit) {
                handleSubmit();
              }
            }}
            onBlur={() => setCapsLockOn(false)}
          />

          {capsLockOn && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Caps Lock is on.
            </Alert>
          )}

          {/* Remember me & forgot password */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Remember me"
            />

            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              onClick={handleForgotPassword}
              sx={{ fontSize: 14, ml: 'auto' }}
            >
              Forgot password?
            </Link>
          </Box>

          {/* Primary CTA */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={!canSubmit || loading}
            onClick={handleSubmit}
            sx={{ height: INPUT_HEIGHT, fontWeight: 700 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Sign in'
            )}
          </Button>
        </Stack>
      </Stack>
    </AuthLayout>
  );
};

export default Login;
